import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { IcUser } from 'components/common/icons/EmojiIcons';
import { AppScreen, Button, ButtonFooter, TextField, FieldGroup } from 'components/ds';
import styles from './GuestLogin.module.css';
import { formatPhone } from 'common/utils/format';

export const GuestLogin = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams<{ meetingId: string }>();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const [nickname, setNickname] = useState('');
    const [phone, setPhone] = useState('');
    const [isLoading, setIsLoading] = useState(false);


    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(formatPhone(e.target.value));
    };

    const isValid = nickname.trim().length > 0 && phone.replace(/[^0-9]/g, '').length >= 10;

    const handleLogin = async () => {
        if (!meetingId || !isValid) return;
        setIsLoading(true);

        try {
            const rawPhone = phone.replace(/[^0-9]/g, '');
            const res = await MeetingRepository.onPostGuestLogin(meetingId, {
                nickname: nickname.trim(),
                phone: rawPhone,
            });

            const data = res?.data?.data || res?.data;
            if (data?.guestToken) {
                // 게스트 세션 저장
                sessionStorage.setItem('guestToken', data.guestToken);
                sessionStorage.setItem('guestUserNo', String(data.guestUserNo));
                sessionStorage.setItem('guestNickname', data.nickname);
                sessionStorage.setItem('guestMeetingId', meetingId);

                navigate(`/meeting/detail/${meetingId}`, { replace: true });
            } else {
                await showAlert({ message: t('guestLogin.alertNotFound') });
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || t('guestLogin.alertNotFoundDetail');
            await showAlert({ message: msg });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AppScreen title={t('guestLogin.title')} bar="sub" hasFooter>
            <header className={styles.hero}>
                <span className={styles.heroIcon}><IcUser size={36} /></span>
                <h1 className={styles.heroTitle}>{t('guestLogin.confirmTitle')}</h1>
                <p className={styles.heroSub}>
                    {t('guestLogin.subtitle1')}<br />
                    {t('guestLogin.subtitle2')}
                </p>
            </header>

            <FieldGroup>
                <TextField
                    label={t('guestLogin.name')}
                    required
                    type="text"
                    placeholder={t('guestLogin.namePlaceholder')}
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={20}
                />
                <TextField
                    label={t('guestLogin.phone')}
                    required
                    type="tel"
                    inputMode="numeric"
                    numeric
                    placeholder="010-0000-0000"
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={13}
                />
            </FieldGroup>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!isValid || isLoading}
                    onClick={handleLogin}
                >
                    {isLoading ? t('guestLogin.checking') : t('guestLogin.viewMeeting')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
